import { PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js"
import PropTypes from "prop-types"
import React, {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
} from "react"
import { SignContractWizardContext } from "src/main/Contracts/SignContract/SignContractWizardContext"

import AlertBanner from "src/components/AlertBanner"
import StripeElements from "src/components/StripeElements"

import {
  cancelPaymentMethodSetup as cancelAccountPaymentMethodSetup,
  createPaymentMethod as createAccountPaymentMethod,
  initializePaymentMethodSetup as initializeAccountPaymentMethodSetup,
} from "src/api/Account/PaymentMethods"
import {
  cancelPaymentMethodSetup as cancelGuestPaymentMethodSetup,
  createPaymentMethod as createGuestPaymentMethod,
  initializePaymentMethodSetup as initializeGuestPaymentMethodSetup,
} from "src/api/ContractQuotes/PaymentMethods"

import useSetupIntent from "src/hooks/payment_method_hooks/use_setup_intent"
import useStripeForm from "src/hooks/payment_method_hooks/use_stripe_form"

const AddMethodForm = forwardRef(
  ({ kind, methodAddedRef, onChange, onCompleteChange, readOnly }, ref) => {
    const { quote, isGuest, wizardCopy } = useContext(SignContractWizardContext)

    const initFn = useMemo(
      () =>
        isGuest
          ? initializeGuestPaymentMethodSetup(quote.encodedId)
          : initializeAccountPaymentMethodSetup,
      [isGuest, quote.encodedId]
    )
    const cancelFn = useMemo(
      () =>
        isGuest
          ? cancelGuestPaymentMethodSetup(quote.encodedId)
          : cancelAccountPaymentMethodSetup,
      [isGuest, quote.encodedId]
    )
    const createFn = useMemo(
      () =>
        isGuest
          ? createGuestPaymentMethod(quote.encodedId)
          : createAccountPaymentMethod,
      [isGuest, quote.encodedId]
    )

    const { activeSetupIntent, error } = useSetupIntent(
      true,
      [kind],
      false,
      initFn,
      cancelFn
    )

    useEffect(() => () => onCompleteChange(false), [onCompleteChange])

    if (error) {
      return (
        <AlertBanner type="error">
          {wizardCopy.paymentSetupUnavailable}
        </AlertBanner>
      )
    }

    if (!activeSetupIntent) return null

    return (
      <StripeElements
        options={{ clientSecret: activeSetupIntent.clientSecret }}
      >
        <StripeSetupForm
          ref={ref}
          setupIntentId={activeSetupIntent.stripeSetupIntentId}
          cancelFn={cancelFn}
          createFn={createFn}
          methodAddedRef={methodAddedRef}
          onChange={onChange}
          onCompleteChange={onCompleteChange}
          readOnly={readOnly}
        />
      </StripeElements>
    )
  }
)

AddMethodForm.displayName = "AddMethodForm"

AddMethodForm.propTypes = {
  kind: PropTypes.oneOf(["card", "us_bank_account"]).isRequired,
  methodAddedRef: PropTypes.shape({ current: PropTypes.bool }).isRequired,
  onChange: PropTypes.func.isRequired,
  onCompleteChange: PropTypes.func.isRequired,
  readOnly: PropTypes.bool.isRequired,
}

const StripeSetupForm = forwardRef(
  (
    {
      setupIntentId,
      cancelFn,
      createFn,
      methodAddedRef,
      onChange,
      onCompleteChange,
      readOnly,
    },
    ref
  ) => {
    const stripe = useStripe()
    const elements = useElements()
    const { confirm, handleChange, complete, confirmedIntentRef } =
      useStripeForm({
        stripe,
        elements,
        createFn,
      })

    useImperativeHandle(
      ref,
      () => ({
        submit: confirm,
        intentConfirmed: () => Boolean(confirmedIntentRef.current),
      }),
      [confirm, confirmedIntentRef]
    )

    // Stripe refuses to cancel an intent it has confirmed, and the card that
    // intent attached is what a retried Continue saves.
    useEffect(() => {
      methodAddedRef.current = false

      return () => {
        // eslint-disable-next-line react-hooks/exhaustive-deps -- read at unmount on purpose
        if (!methodAddedRef.current && !confirmedIntentRef.current) {
          cancelFn(setupIntentId)
        }
      }
    }, [cancelFn, confirmedIntentRef, methodAddedRef, setupIntentId])

    useEffect(() => {
      onCompleteChange(complete)
    }, [complete, onCompleteChange])

    // A locked form only unlocks through "Use a different payment method";
    // an edit event the element emits on its own must not clear that state.
    const handleElementChange = (event) => {
      if (!readOnly) onChange()
      handleChange(event)
    }

    return (
      <PaymentElement
        options={{ layout: "tabs", readOnly }}
        onChange={handleElementChange}
      />
    )
  }
)

StripeSetupForm.displayName = "StripeSetupForm"

StripeSetupForm.propTypes = {
  setupIntentId: PropTypes.string.isRequired,
  cancelFn: PropTypes.func.isRequired,
  createFn: PropTypes.func.isRequired,
  methodAddedRef: PropTypes.shape({ current: PropTypes.bool }).isRequired,
  onChange: PropTypes.func.isRequired,
  onCompleteChange: PropTypes.func.isRequired,
  readOnly: PropTypes.bool.isRequired,
}

export default AddMethodForm
